import React, { useRef, useEffect } from "react";

// Animated, continuously-moving context-graph backdrop for the sign-in screen.
// Nodes drift slowly and edges fade in/out by proximity — evokes DotIQ's
// "connecting fragmented signals" idea. Purely decorative, low-opacity.
const COLORS = ["#22d3ee", "#4ade80", "#a855f7", "#38bdf8", "#2dd4bf", "#c084fc"];

export default function LoginBackground() {
  const canvasRef = useRef(null);
  const raf = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w, h, dpr, nodes;
    const LINK_DIST = 170;
    const PULL_DIST = 220;      // radius within which the pointer influences nodes
    const pointer = { x: -9999, y: -9999, active: false };

    const init = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.max(26, Math.min(48, Math.round((w * h) / 42000)));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        r: 1.6 + Math.random() * 2.6,
        c: COLORS[(Math.random() * COLORS.length) | 0],
      }));
    };

    const step = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter"; // additive -> neon glow
      // edges
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK_DIST) {
            const alpha = (1 - d / LINK_DIST) * 0.55;
            ctx.strokeStyle = `rgba(45,212,191,${alpha})`;
            ctx.shadowColor = "rgba(56,189,248,0.9)";
            ctx.shadowBlur = 6;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      // nodes
      for (const n of nodes) {
        // gentle pull toward the pointer with smooth falloff
        if (pointer.active) {
          const dx = pointer.x - n.x, dy = pointer.y - n.y;
          const d = Math.hypot(dx, dy);
          if (d < PULL_DIST && d > 0.5) {
            const force = (1 - d / PULL_DIST) * 0.06; // soft, calm
            n.vx += (dx / d) * force;
            n.vy += (dy / d) * force;
          }
        }
        // damping keeps drift slow and prevents runaway acceleration
        n.vx *= 0.96; n.vy *= 0.96;
        // maintain a gentle minimum drift so the graph never stalls
        const sp = Math.hypot(n.vx, n.vy);
        if (sp < 0.12) { n.vx += (Math.random() - 0.5) * 0.05; n.vy += (Math.random() - 0.5) * 0.05; }
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
        n.x = Math.max(0, Math.min(w, n.x));
        n.y = Math.max(0, Math.min(h, n.y));
        ctx.beginPath();
        ctx.fillStyle = n.c;
        ctx.shadowColor = n.c;
        ctx.shadowBlur = 14;
        ctx.globalAlpha = 0.95;
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = "source-over";
      raf.current = requestAnimationFrame(step);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      pointer.x = p.clientX - rect.left;
      pointer.y = p.clientY - rect.top;
      pointer.active = true;
    };
    const onLeave = () => { pointer.active = false; };

    init();
    step();
    window.addEventListener("resize", init);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener("resize", init);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <div className="login-bg">
      <canvas ref={canvasRef} className="login-bg-canvas" />
      <div className="login-watermark">Dot<span>IQ</span></div>
    </div>
  );
}
